import type { IconName } from "@verbleif/lib/src/components/Icon/icons";
import type { Component, InjectionKey, Ref } from "vue";

/** The app the debug tools run in. Each app registers its own sections and config adapter. */
export type DebugApp = "auth" | "web" | "cms" | "integrations";

/** Icon of a section in the rail, such as `"server"`. */
export type DebugSectionIcon = IconName;

/** One tab of the debug tools. Create it with `defineDebugSection`. */
export interface DebugSection {
  id: string
  /** Translation key of the rail label. */
  label: string
  icon: DebugSectionIcon
  component: Component
  /** `close` (default) shows only Sluiten; a component replaces the whole footer bar for this section. */
  footer?: "close" | Component
  /** Position in the rail; equal orders keep their registration order. */
  order?: number
}

/**
 * One app config as the debug tools see it. The fields differ per app (see `CONFIG_FIELDS`); every field the form
 * edits is a string, read by key.
 */
export interface DebugAppConfig {
  presetName?: string | null
}

/**
 * The config store of one app, as the debug tools see it. Web, CMS and Integrations keep their override and their
 * debug mode (`developerMode`, localStorage) in the shared config store; the auth app keeps its own override and its
 * debug mode (`debugMode.v1`, sessionStorage).
 */
export interface DebugConfigAdapter {
  /** The config the app runs with: the override when there is one, otherwise the default of this host. */
  config: Readonly<Ref<DebugAppConfig | null>>
  overrideConfig: Readonly<Ref<DebugAppConfig | null>>
  setOverrideConfig: (config: DebugAppConfig) => void
  /** Drops the override, so the app uses its default addresses again. */
  reset: () => void
  debugMode: Ref<boolean>
}

/** How a config field is edited: a URL, free text, or the app environment select. */
export type ConfigFieldType = "url" | "text" | "env";

/** Group heading a field sits under in the Adressen card. */
export type ConfigFieldGroup = "connections" | "login";

export interface ConfigField {
  key: string
  /** Translation key of the label. */
  label: string
  /** Translation key of the placeholder. */
  placeholder: string
  type: ConfigFieldType
  required: true
  group?: ConfigFieldGroup
}

/** What the open dialog offers its sections: the app, its config and navigation inside the dialog. */
export interface DebugToolsContext {
  app: DebugApp
  config: DebugConfigAdapter
  /** Shows another section, such as the Stores link on the Sessie tab. */
  goTo: (sectionId: string) => void
  hasSection: (sectionId: string) => boolean
  close: () => void
  /**
   * State one section shares between its body and its footer, created once per open dialog and kept while the
   * dialog is open (also when the user switches tabs).
   */
  sectionState: <T>(key: string, factory: () => T) => T
}

export const DEBUG_TOOLS_CONTEXT: InjectionKey<DebugToolsContext> = Symbol("debugToolsContext");
